import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  checkLicenceBelongsToUser,
  hasAnyAddressField,
  processDrivingLicense,
  transformDrivingLicenseData,
} from "../../../api/drivingLicenseService";
import Button from "../../../components/common/Button/Button";
import Input from "../../../components/common/Input/Input";
import StickyActionBar from "../../../components/common/stickyActionBar/StickyActionBar";
import AcceptedFilesNote from "../../../components/common/acceptedFilesNote";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import { Button as UiButton } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { cn } from "../../../lib/utils";
import {
  DocumentResultCard,
  DocumentScanCard,
} from "../../motorInsurancePages/fileAClaimFlow/documentCards";
import FormTabs from "../../../components/common/tabs/formTabs";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import {
  fetchBlocksAndAreasList,
  selectBlocksAndAreas,
} from "../../../redux/features/constants/blocksAndAreasListSlice";
import {
  selectVerifyAddressLoading,
  verifyAddress,
} from "../../../redux/features/signUp/addressVerificationSlice";
import { selectCprFromKycVerification } from "../../../redux/features/signUp/kycVerificationStatusSlice";
import performLogout from "../../../utils/logoutHandler";
import "./addressDetailsPage.css";

const EMPTY_ADDRESS = {
  addressType: "FLAT",
  flatNumber: "",
  buildingNumber: "",
  roadNumber: "",
  blockNumber: "",
  area: "",
};

// The API prefixes the stored Home string with this, e.g. "FLAT 12 Building 2470".
const ADDRESS_TYPE_TABS = [
  { type: "FLAT", label_en: "Flat", label_ar: "شقة" },
  { type: "VILLA", label_en: "Villa", label_ar: "فيلا" },
  { type: "OFFICE", label_en: "Office", label_ar: "مكتب" },
];

// flatNumber is optional and only meaningful for a FLAT; area maps to the API's
// `city`, which the backend also derives from the block if we omit it.
const REQUIRED_FIELDS = ["buildingNumber", "roadNumber", "blockNumber", "area"];

// What the licence slot takes; the dropzone and the note below it both read
// this, so the note cannot drift from what is accepted.
const LICENCE_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const LICENCE_MAX_MB = 5;

// Buildings can carry an alpha suffix ("456A"). The API takes the number and the
// letter separately, so accept the natural single input and split it on submit.
const parseBuilding = (raw) => {
  const match = `${raw || ""}`.trim().match(/^(\d+)\s*([A-Za-z]*)$/);
  if (!match) {
    return { buildingNumber: `${raw || ""}`.trim(), buildingAlpha: "" };
  }
  return { buildingNumber: match[1], buildingAlpha: match[2].toUpperCase() };
};

const AddressDetailsPage = ({ onSuccess, pinActionOnMobile = false }) => {
  const translate = useLanguageTranslate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { signupDetails } = useSelector((state) => state.signup);
  const cprFromVerifKyc = useSelector(selectCprFromKycVerification);
  const cprNumber = signupDetails?.cprNumber || cprFromVerifKyc;

  const isSaving = useSelector(selectVerifyAddressLoading);
  const blocksAndAreasData = useSelector(selectBlocksAndAreas);

  const [formData, setFormData] = useState(EMPTY_ADDRESS);
  const [errors, setErrors] = useState({});
  const [licenseFile, setLicenseFile] = useState(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  // null = not attempted, true = fields pre-filled, false = OCR gave us nothing
  const [ocrFilled, setOcrFilled] = useState(null);
  // Set when the licence number (the holder's CPR) is not the signed-in user's.
  // Blocks submission — this is somebody else's licence.
  const [cprMismatch, setCprMismatch] = useState(false);

  useEffect(() => {
    if (!blocksAndAreasData) {
      dispatch(fetchBlocksAndAreasList());
    }
  }, [dispatch, blocksAndAreasData]);

  // This step is a hard verification gate reached with the session token already
  // withheld, so a reload must not resurrect it from storage. Wipe the persisted
  // session on unload; the user comes back to a signed-out app and starts again.
  // Completing the step navigates within the SPA, which never fires this.
  useEffect(() => {
    const forceLogoutOnUnload = () => performLogout();
    window.addEventListener("beforeunload", forceLogoutOnUnload);
    return () => window.removeEventListener("beforeunload", forceLogoutOnUnload);
  }, []);

  // Same block → area lookup the home insurance flow uses.
  const findAreaByBlock = useMemo(() => {
    return (blockNumber) => {
      if (
        !blocksAndAreasData?.areaBlocks ||
        !Array.isArray(blocksAndAreasData.areaBlocks) ||
        !blockNumber
      ) {
        return null;
      }
      return blocksAndAreasData.areaBlocks.find(
        (item) => item.block === blockNumber.toString()
      );
    };
  }, [blocksAndAreasData]);

  const resolveArea = (blockNumber) => {
    const areaData = findAreaByBlock(blockNumber);
    return areaData ? translate(areaData.AreaEn, areaData.AreaAr) : "";
  };

  const validateField = (fieldName, value) => {
    if (REQUIRED_FIELDS.includes(fieldName) && !value) {
      return t("validation.required");
    }
    if (fieldName === "buildingNumber" && value && !/^\d+\s*[A-Za-z]?$/.test(value.trim())) {
      return t("signUpFlow.addressDetailsPage.building_format_error");
    }
    return "";
  };

  // Switching away from FLAT hides the flat field, so drop any value it held
  // rather than submitting "VILLA 12 Building 2470".
  const handleAddressTypeClick = (type) => {
    setFormData((prev) => ({
      ...prev,
      addressType: type,
      flatNumber: type === "FLAT" ? prev.flatNumber : "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      // Typing a block auto-fills the area, but never blanks an area the user
      // already has — an unknown block should not wipe a good value.
      if (name === "blockNumber") {
        const resolvedArea = resolveArea(value);
        if (resolvedArea) {
          next.area = resolvedArea;
        }
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleFileSelect = async (fileData) => {
    const file = fileData?.file;
    if (!file) return;

    setLicenseFile(fileData);
    setIsProcessingOCR(true);
    setCprMismatch(false);

    try {
      const result = await processDrivingLicense(file);
      const transformed = result?.success
        ? transformDrivingLicenseData(result.data)
        : null;

      // Identity gate: the licence number is the holder's CPR, so a mismatch
      // means this licence is not the signed-in user's. Refuse it outright and
      // keep the form empty rather than pre-filling a stranger's address.
      const { verdict } = checkLicenceBelongsToUser(transformed, cprNumber);
      if (verdict === "mismatch") {
        setFormData((prev) => ({ ...EMPTY_ADDRESS, addressType: prev.addressType }));
        setErrors({});
        setCprMismatch(true);
        setOcrFilled(null);
        toast.error(
          translate(
            "This licence belongs to a different CPR. Please upload your own driving licence.",
            "هذه الرخصة تعود لرقم شخصي مختلف. يرجى تحميل رخصة القيادة الخاصة بك."
          )
        );
        return;
      }

      if (hasAnyAddressField(transformed)) {
        setFormData((prev) => {
          const next = {
            // Spread first: replacing the object outright would drop
            // addressType, unselecting the tab and sending it as undefined.
            ...prev,
            flatNumber: transformed.flatNumber || prev.flatNumber,
            buildingNumber: transformed.buildingNumber || prev.buildingNumber,
            roadNumber: transformed.roadNumber || prev.roadNumber,
            blockNumber: transformed.blockNumber || prev.blockNumber,
            area: transformed.area || prev.area,
          };

          // Prefer our own block→area mapping over whatever the OCR read.
          const resolvedArea = resolveArea(next.blockNumber);
          if (resolvedArea) {
            next.area = resolvedArea;
          }

          return next;
        });
        setErrors({});
        setOcrFilled(true);
      } else {
        // OCR is non-blocking everywhere else in the app; keep that here.
        setOcrFilled(false);
      }
    } catch (error) {
      console.error("[Vista] Driving licence OCR failed:", error);
      setOcrFilled(false);
    } finally {
      setIsProcessingOCR(false);
    }
  };

  const handleFileRemove = () => {
    setLicenseFile(null);
    setOcrFilled(null);
    setCprMismatch(false);
  };

  const validateForm = () => {
    const newErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmitHandler = async (e) => {
    e?.preventDefault();

    if (!licenseFile) {
      toast.error(
        translate(
          "Please upload your driving licence.",
          "يرجى تحميل رخصة القيادة الخاصة بك."
        )
      );
      return;
    }

    if (cprMismatch) {
      toast.error(
        translate(
          "This licence belongs to a different CPR. Please upload your own driving licence.",
          "هذه الرخصة تعود لرقم شخصي مختلف. يرجى تحميل رخصة القيادة الخاصة بك."
        )
      );
      return;
    }

    if (!validateForm()) return;

    const { buildingNumber, buildingAlpha } = parseBuilding(
      formData.buildingNumber
    );

    // `country` is omitted so the API applies its "BAHRAIN" default, and
    // `USCitizen` is omitted because the FATCA declaration was dropped with the
    // BENEFIT retirement — the API still accepts it if it ever comes back.
    const payload = {
      CPR: cprNumber,
      addressType: formData.addressType,
      buildingNumber,
      roadNumber: formData.roadNumber,
      blockNumber: formData.blockNumber,
      city: formData.area,
    };

    if (buildingAlpha) {
      payload.buildingAlpha = buildingAlpha;
    }
    if (formData.addressType === "FLAT" && formData.flatNumber) {
      payload.flatNumber = formData.flatNumber;
    }

    const response = await dispatch(verifyAddress(payload));

    if (response?.type === "verifyAddress/fulfilled") {
      onSuccess();
    } else {
      toast.error(
        translate(
          response?.payload?.ErrorMessageEn ||
            "We couldn't save your details. Please try again.",
          response?.payload?.ErrorMessageAr ||
            "لم نتمكن من حفظ بياناتك. الرجاء المحاولة مرة أخرى."
        )
      );
    }
  };

  const isBusy = isProcessingOCR || isSaving;

  // Same dropzone the claim flow uses. One file, the licence.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => accepted[0] && handleFileSelect({ file: accepted[0] }),
    onDropRejected: () =>
      toast.error(
        translate(
          `Please upload a JPG, PNG or PDF up to ${LICENCE_MAX_MB}MB.`,
          `يرجى تحميل ملف JPG أو PNG أو PDF بحجم لا يتجاوز ${LICENCE_MAX_MB} ميغابايت.`
        )
      ),
    accept: Object.fromEntries(LICENCE_TYPES.map((type) => [type, []])),
    maxSize: LICENCE_MAX_MB * 1024 * 1024,
    multiple: false,
    disabled: isBusy,
  });

  // Submit is ready only when there is something to submit, as on the other
  // steps of sign-up (it used to look pressable on an empty form).
  const isComplete =
    Boolean(licenseFile) &&
    REQUIRED_FIELDS.every((field) => `${formData[field] || ""}`.trim());

  // One line of feedback under the upload box, so the OCR outcome never shifts
  // the fields below it around.
  const uploadStatus = () => {
    // While reading, the scan card and the Submit button carry the wait.
    if (isProcessingOCR) return null;
    if (cprMismatch) {
      return {
        text: t("signUpFlow.addressDetailsPage.cpr_mismatch_label"),
        tone: "text-red-500 font-semibold",
      };
    }
    if (ocrFilled === true) {
      return {
        text: t("signUpFlow.addressDetailsPage.filled_label"),
        tone: "text-[#00384D] font-semibold",
      };
    }
    if (ocrFilled === false) {
      return {
        text: t("signUpFlow.addressDetailsPage.manual_entry_label"),
        tone: "text-[#5B6871]",
      };
    }
    // Nothing to say before an upload: the dropzone names what it takes.
    return null;
  };

  const status = uploadStatus();

  return (
    <>
      {/* Header is fixed at 48px (56px on mobile); the title starts 24px below
          it on a phone, at 80px like every other step of sign-up. There is
          deliberately no in-page back link: leaving this step must go through
          the header's close button, which logs the user out. Saving shows in
          the Submit button, not a loader over the page. */}
      <div className="address_details_page flex justify-center w-full overflow-y-auto mt-16 max767:mt-[80px] ipad:mt-16 px-5 pb-10 max767:px-4">
        <div className="w-full max-w-[560px]">
          <TitleDescription
            className="mb-6"
            title={t("signUpFlow.addressDetailsPage.page_title")}
            description={t("signUpFlow.addressDetailsPage.page_description")}
          />

          <form onSubmit={formSubmitHandler}>
            {/* Identity, shown for confidence only. The CPR comes from the
                authenticated session and the licence number is read off the
                document — neither is user-editable, and neither is taken from
                this field on submit. */}
            <div className="mb-5">
              <Input
                label={t("signUpFlow.addressDetailsPage.cpr_input_label")}
                type="text"
                name="cpr"
                value={cprNumber || ""}
                handleChange={() => {}}
                disabled
                readOnly
                className="!bg-[#F6F8F9] !text-[#5B6871] !cursor-not-allowed"
                data-testid="cpr"
              />
              {/* Rendered here rather than through Input's hint_msg, which is
                  an unstyled <small> that picks up black text and a 26px line. */}
              <p className="mt-1.5 text-[#5B6871] text-sm leading-5">
                {t("signUpFlow.addressDetailsPage.cpr_hint")}
              </p>
            </div>

            {/* Licence upload */}
            <div className="mb-5">
              <label className="block text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-1">
                {t("signUpFlow.addressDetailsPage.upload_label")}
              </label>
              {!licenseFile ? (
                <Card
                  {...getRootProps({
                    className: cn(
                      "dropzone group flex flex-col items-center justify-center gap-2.5",
                      "min-h-[148px] px-5 py-5 text-center cursor-pointer",
                      "rounded-[10px] border-2 border-dashed shadow-none",
                      "transition-[background-color,border-color] duration-200 ease-out",
                      "motion-reduce:transition-none",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      isDragActive
                        ? "border-primary bg-[#F0FAFF]"
                        : "border-border bg-muted hover:border-primary/45 hover:bg-[#F4FAFF]"
                    ),
                  })}
                >
                  <input {...getInputProps()} />
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]",
                        "transition-colors duration-200 ease-out motion-reduce:transition-none",
                        isDragActive
                          ? "bg-primary/20"
                          : "bg-primary/10 group-hover:bg-primary/15"
                      )}
                    >
                      <Upload
                        className={cn(
                          "h-[18px] w-[18px]",
                          isDragActive ? "text-[#055B80]" : "text-[#076A96]"
                        )}
                      />
                    </span>
                    <p className="text-[#252C32] text-base not-italic font-semibold leading-6 text-start">
                      {isDragActive
                        ? translate("Drop to upload", "أفلت الملف للتحميل")
                        : translate("Drag and drop your licence here", "اسحب رخصتك هنا")}
                    </p>
                  </div>
                  <UiButton
                    type="button"
                    variant="outline"
                    tabIndex={-1}
                    className={cn(
                      "h-9 px-4 text-sm font-semibold tracking-[-0.084px]",
                      "border-[#CFD8DC] bg-white text-[#48535B]",
                      "shadow-[0_1px_2px_rgba(11,47,66,0.06)]",
                      "hover:bg-white hover:border-[#9FDCF5] hover:text-[#076A96]",
                      "motion-reduce:transition-none"
                    )}
                  >
                    {translate("Browse", "تصفح")}
                  </UiButton>
                  <AcceptedFilesNote
                    types={LICENCE_TYPES}
                    maxSizeMb={LICENCE_MAX_MB}
                  />
                </Card>
              ) : isProcessingOCR ? (
                <DocumentScanCard file={licenseFile.file} mode="analyzing" />
              ) : (
                <DocumentResultCard
                  file={licenseFile.file}
                  state={
                    cprMismatch
                      ? "wrong_document"
                      : ocrFilled
                      ? "verified"
                      : "uploaded"
                  }
                  justRead
                  onRemove={handleFileRemove}
                />
              )}
              {status && (
                <p className={`text-sm not-italic font-normal leading-5 mt-2 ${status.tone}`}>
                  {status.text}
                </p>
              )}
            </div>

            {/* Address fields */}
            <div className="border-t border-solid border-[#DDE2E4] pt-5 mb-5">
              {/* A step above the 16px field labels it heads, so the section
                  reads as a section and not as one more label. */}
              <h2 className="text-[#1A2024] text-lg not-italic font-semibold leading-7 tracking-[-0.18px] mb-3">
                {t("signUpFlow.addressDetailsPage.section_label")}
              </h2>

              <div className="mb-4">
                <FormTabs
                  tabs={ADDRESS_TYPE_TABS}
                  activeTab={formData.addressType}
                  onTabClick={handleAddressTypeClick}
                  label={t("signUpFlow.addressDetailsPage.type_label")}
                />
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-4 max-350:grid-cols-1">
                {formData.addressType === "FLAT" && (
                  <Input
                    label={t("signUpFlow.addressDetailsPage.flat_input_label")}
                    type="text"
                    inputMode="numeric"
                    name="flatNumber"
                    value={formData.flatNumber}
                    handleChange={handleChange}
                    error={errors.flatNumber}
                    disabled={isBusy}
                    data-testid="flatNumber"
                  />
                )}
                <Input
                  label={t(
                    "signUpFlow.addressDetailsPage.building_input_label"
                  )}
                  type="text"
                  name="buildingNumber"
                  value={formData.buildingNumber}
                  handleChange={handleChange}
                  error={errors.buildingNumber}
                  disabled={isBusy}
                  inputClassName={
                    formData.addressType === "FLAT"
                      ? ""
                      : "col-span-2 max-350:col-span-1"
                  }
                  data-testid="buildingNumber"
                />
                <Input
                  label={t("signUpFlow.addressDetailsPage.road_input_label")}
                  type="text"
                  inputMode="numeric"
                  name="roadNumber"
                  value={formData.roadNumber}
                  handleChange={handleChange}
                  error={errors.roadNumber}
                  disabled={isBusy}
                  data-testid="roadNumber"
                />
                <Input
                  label={t("signUpFlow.addressDetailsPage.block_input_label")}
                  type="text"
                  inputMode="numeric"
                  name="blockNumber"
                  value={formData.blockNumber}
                  handleChange={handleChange}
                  error={errors.blockNumber}
                  disabled={isBusy}
                  data-testid="blockNumber"
                />
                <Input
                  label={t("signUpFlow.addressDetailsPage.area_input_label")}
                  type="text"
                  name="area"
                  value={formData.area}
                  handleChange={handleChange}
                  error={errors.area}
                  disabled={isBusy}
                  inputClassName="col-span-2 max-350:col-span-1"
                  data-testid="area"
                />
              </div>
            </div>

            <p className="text-[#5B6871] text-sm not-italic font-normal leading-5 mb-4">
              {t("signUpFlow.addressDetailsPage.confirm_hint")}
            </p>

            <StickyActionBar pinned={pinActionOnMobile}>
              <Button
                type="submit"
                label={t("signUpFlow.addressDetailsPage.btn_label")}
                className="primary-button !mb-0"
                disabled={isBusy || cprMismatch || !isComplete}
                isLoading={isBusy}
              />
            </StickyActionBar>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddressDetailsPage;
